// src/pages/Farmer/EditFarmer.jsx
import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import farmerService from '../../services/farmer.service';
import geoService from '../../services/geo.service';
import SearchableSelect from '../../components/SearchableSelect/SearchableSelect';
import Toast from '../../components/Toast/Toast';
import '../../styles/AddPages.css';

const CROP_OPTIONS = ['Wheat','Rice','Cotton','Sugarcane','Soybean','Maize','Banana','Tomato','Onion','Potato'];
const ACRE_OPTIONS = ['1 to 3 Acres','4 to 6 Acres','7 to 10 Acres','10 to 20 Acres','Above 20 Acres','Above 100 Acres'];

const EditFarmer = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isSuperAdmin } = useAuth();

  const [formData, setFormData] = useState({
    farmerName: '',
    mobileNo: '',
    headquarterId: '',
    state: '',
    district: '',
    taluka: '',
    villageMasterId: '',
    crop: '',
    acre: '',
    latitude: '',
    longitude: '',
    currentDate: '',
    travelingWith: '',
  });

  const [headquarters, setHeadquarters] = useState([]);
  const [states,       setStates]       = useState([]);
  const [districts,    setDistricts]    = useState([]);
  const [talukas,      setTalukas]      = useState([]);
  const [villages,     setVillages]     = useState([]);
  const [travelingWithOptions, setTraveling] = useState([]);

  const [loadingFarmer, setLoadingFarmer] = useState(true);
  const [loading, setLoading] = useState({ hqs:false, dt:false, tl:false, vl:false });
  const [errors, setErrors]   = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [toast, setToast]     = useState(null);

  // Track manual changes so we don't wipe pre-loaded values on initial mount
  const [stateChangedByUser,    setStateChangedByUser]    = useState(false);
  const [districtChangedByUser, setDistrictChangedByUser] = useState(false);
  const [talukaChangedByUser,   setTalukaChangedByUser]   = useState(false);

  // ── Load farmer + HQs + traveling-with + states on mount ─────────────────
  useEffect(() => {
    const cid = isSuperAdmin ? localStorage.getItem('selectedCompanyId') : null;

    const loadFarmer = async () => {
      try {
        const res = await farmerService.getFarmerById(id);
        if (res.success) {
          const f = res.data;
          const vm = f.villageRef;  // backend should return relation
          setFormData({
            farmerName:      f.farmerName || '',
            mobileNo:        f.mobileNo   || '',
            headquarterId:   f.headquarterId ? String(f.headquarterId) : '',
            state:           vm?.stateName    || '',
            district:        vm?.districtName || '',
            taluka:          vm?.talukaName   || '',
            villageMasterId: f.villageMasterId ? String(f.villageMasterId) : '',
            crop:            f.crop  || '',
            acre:            f.acre  || '',
            latitude:        f.latitude  ?? '',
            longitude:       f.longitude ?? '',
            currentDate:     f.currentDate
              ? new Date(f.currentDate).toISOString().split('T')[0]
              : new Date().toISOString().split('T')[0],
            travelingWith:   f.travelingWith ? String(f.travelingWith) : '',
          });

          // Pre-load the cascade dropdowns based on existing values
          if (vm?.stateName) {
            const dRes = await geoService.getDistricts(vm.stateName);
            if (dRes.success) setDistricts(dRes.data || []);
          }
          if (vm?.stateName && vm?.districtName) {
            const tRes = await geoService.getTalukas(vm.stateName, vm.districtName);
            if (tRes.success) setTalukas(tRes.data || []);
          }
          if (vm?.stateName && vm?.districtName && vm?.talukaName) {
            const vRes = await geoService.getVillages(vm.stateName, vm.districtName, vm.talukaName);
            if (vRes.success) setVillages(vRes.data || []);
          }
        }
      } catch {
        setToast({ message: 'Failed to load farmer data', type: 'error' });
      } finally {
        setLoadingFarmer(false);
      }
    };

    const loadHQs = async () => {
      setLoading(l => ({ ...l, hqs: true }));
      try {
        const res = await farmerService.getMyHeadquarters(cid);
        if (res.success) setHeadquarters(res.data || []);
      } catch {
        setToast({ message: 'Failed to load HQs', type: 'error' });
      } finally {
        setLoading(l => ({ ...l, hqs: false }));
      }
    };

    const loadStates = async () => {
      try {
        const res = await geoService.getStates();
        if (res.success) setStates(res.data || []);
      } catch {}
    };

    const loadTravelingWith = async () => {
      try {
        const res = await farmerService.getTravelingWithEmployees(cid);
        if (res.success) setTraveling(res.data || []);
      } catch {}
    };

    loadFarmer();
    loadHQs();
    loadStates();
    loadTravelingWith();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isSuperAdmin]);

  // ── State → districts (only after user manually changes) ─────────────────
  useEffect(() => {
    if (!stateChangedByUser) return;
    setDistricts([]); setTalukas([]); setVillages([]);
    setFormData(f => ({ ...f, district:'', taluka:'', villageMasterId:'' }));
    if (!formData.state) return;
    setLoading(l => ({ ...l, dt: true }));
    geoService.getDistricts(formData.state)
      .then(r => { if (r.success) setDistricts(r.data || []); })
      .finally(() => setLoading(l => ({ ...l, dt: false })));
  }, [formData.state, stateChangedByUser]);

  // ── District → talukas ───────────────────────────────────────────────────
  useEffect(() => {
    if (!districtChangedByUser) return;
    setTalukas([]); setVillages([]);
    setFormData(f => ({ ...f, taluka:'', villageMasterId:'' }));
    if (!formData.state || !formData.district) return;
    setLoading(l => ({ ...l, tl: true }));
    geoService.getTalukas(formData.state, formData.district)
      .then(r => { if (r.success) setTalukas(r.data || []); })
      .finally(() => setLoading(l => ({ ...l, tl: false })));
  }, [formData.district, districtChangedByUser]);

  // ── Taluka → villages ────────────────────────────────────────────────────
  useEffect(() => {
    if (!talukaChangedByUser) return;
    setVillages([]);
    setFormData(f => ({ ...f, villageMasterId:'' }));
    if (!formData.state || !formData.district || !formData.taluka) return;
    setLoading(l => ({ ...l, vl: true }));
    geoService.getVillages(formData.state, formData.district, formData.taluka)
      .then(r => { if (r.success) setVillages(r.data || []); })
      .finally(() => setLoading(l => ({ ...l, vl: false })));
  }, [formData.taluka, talukaChangedByUser]);

  // ── Handlers ─────────────────────────────────────────────────────────────
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const detectLocation = () => {
  if (!navigator.geolocation) {
    setToast({ message: 'Geolocation is not supported by your browser.', type: 'error' });
    return;
  }

  setToast({ message: 'Detecting location…', type: 'info' });

  let bestReading = null;
  let settled = false;

  const finish = (position) => {
    if (settled) return;
    settled = true;
    navigator.geolocation.clearWatch(watchId);

    const { latitude, longitude, accuracy } = position.coords;
    setFormData(prev => ({
      ...prev,
      latitude: latitude.toFixed(8),
      longitude: longitude.toFixed(8),
      locationAccuracy: accuracy, // store this on the entity too
    }));

    setToast({ message: '📍 Location detected!', type: 'success' });
  };

  const watchId = navigator.geolocation.watchPosition(
    (position) => {
      if (!bestReading || position.coords.accuracy < bestReading.coords.accuracy) {
        bestReading = position;
      }
      // good enough — stop early
      if (position.coords.accuracy <= 15) {
        finish(position);
      }
    },
    (error) => {
      if (settled) return;
      settled = true;
      navigator.geolocation.clearWatch(watchId);
      const message =
        error.code === error.PERMISSION_DENIED
          ? 'Location access denied. Please enable it in your browser/app settings.'
          : error.code === error.TIMEOUT
          ? 'Location request timed out. Please try again.'
          : 'Unable to detect location. ';
      setToast({ message, type: 'error' });
      console.error('Geolocation error:', error);
    },
    { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
  );

  // Hard stop after ~12s — use best reading we've got rather than hang forever
  setTimeout(() => {
    if (!settled && bestReading) {
      finish(bestReading);
    } else if (!settled) {
      settled = true;
      navigator.geolocation.clearWatch(watchId);
      setToast({ message: 'Could not get an accurate location. ', type: 'error' });
    }
  }, 12000);
};

  const validate = () => {
    const e = {};
    if (!formData.farmerName.trim())              e.farmerName    = 'Farmer name is required';
    if (!formData.mobileNo.trim())                e.mobileNo      = 'Mobile is required';
    else if (!/^\d{10}$/.test(formData.mobileNo)) e.mobileNo      = 'Must be 10 digits';
    if (!formData.headquarterId)                  e.headquarterId = 'Headquarter is required';
    if (!formData.state)                          e.state         = 'State is required';
    if (!formData.district)                       e.district      = 'District is required';
    if (!formData.taluka)                         e.taluka        = 'Tehsil is required';
    if (!formData.villageMasterId)                e.villageMasterId = 'Village is required';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setIsSubmitting(true);
    try {
      const pickedVillage = villages.find(v => String(v.id) === String(formData.villageMasterId));

      const payload = {
        ...formData,
        headquarterId:   parseInt(formData.headquarterId, 10),
        villageRefId: parseInt(formData.villageMasterId, 10),
        village:         pickedVillage?.name || '',
        villageCode:     pickedVillage?.code || '',
        travelingWith:   formData.travelingWith !== '' ? Number(formData.travelingWith) : null,
      };

      if (isSuperAdmin) {
        const cid = localStorage.getItem('selectedCompanyId');
        if (!cid) {
          setToast({ message: 'Please select a company first', type: 'error' });
          setIsSubmitting(false);
          return;
        }
        payload.companyId = parseInt(cid, 10);
      }

      const res = await farmerService.updateFarmer(id, payload);
      if (res.success) {
        setToast({ message: 'Farmer updated successfully!', type: 'success' });
        setTimeout(() => navigate('/farmer/view'), 1200);
      }
    } catch (err) {
      setToast({
        message: err.response?.data?.message || 'Failed to update farmer',
        type: 'error',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loadingFarmer) {
    return (
      <div className="add-farmer-page">
        <div style={{ textAlign:'center', padding:40 }}>
          <p>Loading farmer data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="add-farmer-page">
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

      <nav aria-label="breadcrumb">
        <ol className="breadcrumb">
          <li className="breadcrumb-item"><a href="/dashboard/overview">Dashboard</a></li>
          <li className="breadcrumb-item"><a href="/farmer/view">Farmers</a></li>
          <li className="breadcrumb-item active">Edit Farmer</li>
        </ol>
      </nav>

      <div className="page-header"><h4 className="page-title">Edit Farmer</h4></div>

      <div className="form-card">
        <form onSubmit={handleSubmit}>

          {/* Basic info */}
          <div className="form-section">
            <h3 className="section-title">👤 Farmer Info</h3>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Farmer Name <span className="required">*</span></label>
                <input type="text" name="farmerName"
                  className={`form-input ${errors.farmerName ? 'error' : ''}`}
                  value={formData.farmerName} onChange={handleChange}/>
                {errors.farmerName && <span className="error-message">{errors.farmerName}</span>}
              </div>

              <div className="form-group">
                <label className="form-label">Mobile No <span className="required">*</span></label>
                <input type="tel" name="mobileNo" maxLength="10"
                  className={`form-input ${errors.mobileNo ? 'error' : ''}`}
                  value={formData.mobileNo} onChange={handleChange}/>
                {errors.mobileNo && <span className="error-message">{errors.mobileNo}</span>}
              </div>

              <div className="form-group">
                <SearchableSelect
                  label="Headquarter"
                  required
                  options={headquarters}
                  value={formData.headquarterId}
                  onChange={(hqId) => {
                    setFormData(prev => ({ ...prev, headquarterId: hqId ? String(hqId) : '' }));
                    if (errors.headquarterId) setErrors(prev => ({ ...prev, headquarterId: '' }));
                  }}
                  loading={loading.hqs}
                  placeholder={
                    headquarters.length === 0 && !loading.hqs
                      ? 'No HQs assigned'
                      : 'Search headquarter'
                  }
                  emptyMsg="No headquarters found"
                  error={errors.headquarterId}
                />
              </div>
            </div>
          </div>

          {/* Location cascade */}
          <div className="form-section">
            <h3 className="section-title">📍 Location</h3>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">State <span className="required">*</span></label>
                <select name="state" className={`form-input ${errors.state ? 'error' : ''}`}
                  value={formData.state}
                  onChange={(e) => {
                    setStateChangedByUser(true);
                    setDistrictChangedByUser(false);
                    setTalukaChangedByUser(false);
                    setFormData(f => ({ ...f, state: e.target.value }));
                  }}>
                  <option value="">Select State</option>
                  {states.map(s => <option key={s.name} value={s.name}>{s.name}</option>)}
                </select>
                {errors.state && <span className="error-message">{errors.state}</span>}
              </div>

              <div className="form-group">
                <label className="form-label">District <span className="required">*</span></label>
                {loading.dt ? (
                  <div className="form-input" style={{ color:'#adb5bd' }}>Loading...</div>
                ) : (
                  <select name="district" className={`form-input ${errors.district ? 'error' : ''}`}
                    value={formData.district}
                    onChange={(e) => {
                      setDistrictChangedByUser(true);
                      setTalukaChangedByUser(false);
                      setFormData(f => ({ ...f, district: e.target.value }));
                    }}
                    disabled={!formData.state}>
                    <option value="">{!formData.state ? 'Select State first' : 'Select District'}</option>
                    {districts.map(d => <option key={d.name} value={d.name}>{d.name}</option>)}
                  </select>
                )}
                {errors.district && <span className="error-message">{errors.district}</span>}
              </div>

              <div className="form-group">
                <label className="form-label">Tehsil/Taluka <span className="required">*</span></label>
                {loading.tl ? (
                  <div className="form-input" style={{ color:'#adb5bd' }}>Loading...</div>
                ) : (
                  <select name="taluka" className={`form-input ${errors.taluka ? 'error' : ''}`}
                    value={formData.taluka}
                    onChange={(e) => {
                      setTalukaChangedByUser(true);
                      setFormData(f => ({ ...f, taluka: e.target.value }));
                    }}
                    disabled={!formData.district}>
                    <option value="">{!formData.district ? 'Select District first' : 'Select Tehsil'}</option>
                    {talukas.map(t => <option key={t.name} value={t.name}>{t.name}</option>)}
                  </select>
                )}
                {errors.taluka && <span className="error-message">{errors.taluka}</span>}
              </div>
            </div>

            <div className="form-row">
              <div className="form-group form-group-full">
                <SearchableSelect
                  label="Village"
                  required
                  options={villages.map(v => ({ id: v.id, name: v.code ? `${v.name} (${v.code})` : v.name }))}
                  value={formData.villageMasterId}
                  onChange={(vid) => {
                    setFormData(prev => ({ ...prev, villageMasterId: vid ? String(vid) : '' }));
                    if (errors.villageMasterId) setErrors(prev => ({ ...prev, villageMasterId: '' }));
                  }}
                  disabled={!formData.taluka}
                  loading={loading.vl}
                  placeholder={!formData.taluka ? 'Select Tehsil first' : `Search ${villages.length} villages`}
                  emptyMsg="No villages found"
                  error={errors.villageMasterId}
                />
              </div>
            </div>
          </div>

          {/* Farm details */}
          <div className="form-section">
            <h3 className="section-title">🌾 Farm Details</h3>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Crop</label>
                <select name="crop" className="form-input" value={formData.crop} onChange={handleChange}>
                  <option value="">Select Crop</option>
                  {CROP_OPTIONS.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>

              <div className="form-group">
                <label className="form-label">Acre</label>
                <select name="acre" className="form-input" value={formData.acre} onChange={handleChange}>
                  <option value="">Select Acre Range</option>
                  {ACRE_OPTIONS.map(a => <option key={a} value={a}>{a}</option>)}
                </select>
              </div>

              <div className="form-group">
                <label className="form-label">Current Date</label>
                <input type="date" name="currentDate" className="form-input"
                  value={formData.currentDate} onChange={handleChange}/>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Latitude</label>
                <div style={{ display:'flex', gap:8 }}>
                  <input type="text" name="latitude" className="form-input" style={{ flex:1 }}
                    value={formData.latitude} onChange={handleChange}/>
                  <button type="button" className="btn btn-secondary" onClick={detectLocation}>📍 Detect</button>
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Longitude</label>
                <input type="text" name="longitude" className="form-input"
                  value={formData.longitude} onChange={handleChange}/>
              </div>

              <div className="form-group">
                <label className="form-label">Traveling With</label>
                <select name="travelingWith" className="form-input"
                  value={formData.travelingWith} onChange={handleChange}>
                  <option value="">Select Name</option>
                  {travelingWithOptions.map(emp => (
                    <option key={emp.id} value={emp.id}>{emp.firstName} {emp.lastName}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          <div className="form-actions">
            <button type="button" className="btn btn-secondary"
              onClick={() => navigate('/farmer/view')} disabled={isSubmitting}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
              {isSubmitting ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditFarmer;